import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { hrAPI } from '../../../api/hr';
import { DEFAULT_PAGINATION } from '../constants';
import SalaryFormModal from './SalaryFormModal';

const PAGE_SIZE = 10;

const formatMoney = (value, currency = 'INR') => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const computeTotal = (obj = {}) =>
  Object.values(obj || {}).reduce((acc, value) => acc + Number(value || 0), 0);

const SalaryTab = ({
  employeeOptions = [],
  employeesLoading = false,
  showSuccess,
  showError,
  onDataChanged
}) => {
  const [filters, setFilters] = useState({ user: '', isActive: '', page: 1 });
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ open: false, mode: 'create', record: null });
  const [deletingId, setDeletingId] = useState(null);

  const fetchSalaries = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: filters.page,
        limit: PAGE_SIZE,
        user: filters.user || undefined
      };
      if (filters.isActive !== '') {
        params.isActive = filters.isActive;
      }
      const response = await hrAPI.listSalaries(params);
      setRecords(response?.items || []);
      setPagination({
        ...DEFAULT_PAGINATION,
        limit: PAGE_SIZE,
        ...(response?.pagination || {})
      });
    } catch (error) {
      showError?.(error.message || 'Failed to load salary records');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  const totalPages = useMemo(() => Math.max(pagination.pages || 1, 1), [pagination.pages]);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handlePageChange = (direction) => {
    setFilters((prev) => ({
      ...prev,
      page: Math.min(Math.max(prev.page + direction, 1), totalPages)
    }));
  };

  const handleCreate = async (payload) => {
    try {
      await hrAPI.createSalary(payload);
      showSuccess?.('Salary profile created');
      await fetchSalaries();
      onDataChanged?.();
      return true;
    } catch (error) {
      const errorMessage = error.message || 'Unable to create salary profile';
      showError?.(errorMessage);
      throw error; // Re-throw so modal can capture it
    }
  };

  const handleUpdate = async (payload) => {
    if (!modalState.record?._id) return false;
    try {
      await hrAPI.updateSalary(modalState.record._id, payload);
      showSuccess?.('Salary profile updated');
      await fetchSalaries();
      onDataChanged?.();
      return true;
    } catch (error) {
      const errorMessage = error.message || 'Unable to update salary';
      showError?.(errorMessage);
      throw error; // Re-throw so modal can capture it
    }
  };

  const handleDelete = async (record) => {
    if (!record?._id) return;
    if (!window.confirm('Delete this salary profile?')) {
      return;
    }
    try {
      setDeletingId(record._id);
      await hrAPI.deleteSalary(record._id);
      showSuccess?.('Salary profile deleted');
      await fetchSalaries();
      onDataChanged?.();
    } catch (error) {
      showError?.(error.message || 'Unable to delete salary');
    } finally {
      setDeletingId(null);
    }
  };

  const currentModalSubmit = modalState.mode === 'edit' ? handleUpdate : handleCreate;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Salary Management</h3>
          <p className="text-sm text-gray-500">Maintain structured compensation for every employee</p>
        </div>
        <button
          onClick={() => setModalState({ open: true, mode: 'create', record: null })}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Add Salary Profile
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Employee</label>
            <select
              value={filters.user}
              onChange={(event) => updateFilter('user', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">
                {employeesLoading ? 'Loading employees...' : 'All employees'}
              </option>
              {employeeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
            <select
              value={filters.isActive}
              onChange={(event) => updateFilter('isActive', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-end justify-end">
            <button
              onClick={() => setFilters({ user: '', isActive: '', page: 1 })}
              className="text-sm text-blue-600 hover:underline"
            >
              Reset filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Category/Location</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">CTC & Wage</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Compensation</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Net Salary</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Status</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="Loader2" size={20} className="animate-spin text-blue-600" />
                      <span>Loading salary profiles...</span>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                    No salary profiles found
                  </td>
                </tr>
              ) : (
                records.map((record) => {
                  const currency = record.currency || 'INR';
                  const totalAllowances = record.totalAllowances ?? computeTotal(record.allowances);
                  const totalDeductions = record.totalDeductions ?? computeTotal(record.deductions);
                  const pfTotal = (record.pf?.employeeContribution || 0) + (record.pf?.employerContribution || 0);
                  const esicTotal = (record.esic?.employeeContribution || 0) + (record.esic?.employerContribution || 0);
                  
                  // Determine employment type (with backward compatibility)
                  let employmentType = record.employmentType || 'Salaried';
                  if (!record.employmentType && record.hourlyRate && record.hourlyRate > 0) {
                    employmentType = 'Hourly';
                  } else if (!record.employmentType && record.perVisitRate && record.perVisitRate > 0) {
                    employmentType = 'Per-Visit';
                  }
                  
                  return (
                    <tr key={record._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-medium">{record.user ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim() || record.user.email : '—'}</div>
                        <div className="text-xs text-gray-500">{record.user?.role}</div>
                        {record.designation && <div className="text-xs text-gray-500">{record.designation}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-medium">{record.workerCategory || 'Permanent'}</div>
                        <div className="text-xs text-gray-500">{record.workLocationState || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {record.effectiveFrom ? new Date(record.effectiveFrom).toLocaleDateString() : '-'}
                          {record.effectiveTo && ` to ${new Date(record.effectiveTo).toLocaleDateString()}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-medium">CTC: {formatMoney(record.ctc, currency)}</div>
                        <div className="text-xs text-gray-500">Min Wage: {formatMoney(record.minimumWage, currency)}</div>
                        <div className="text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${record.isMinimumWageCompliant ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {record.isMinimumWageCompliant ? '✓ Compliant' : '✗ Non-compliant'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {employmentType === 'Hourly' ? (
                          <>
                            <div className="font-medium text-purple-600">Hourly: {formatMoney(record.hourlyRate, currency)}/hr</div>
                            {record.expectedHoursPerPeriod > 0 && (
                              <div className="text-xs text-gray-500">Est. {record.expectedHoursPerPeriod} hrs/{record.paymentFrequency?.toLowerCase() || 'period'}</div>
                            )}
                          </>
                        ) : employmentType === 'Per-Visit' ? (
                          <>
                            <div className="font-medium text-indigo-600">Per Visit: {formatMoney(record.perVisitRate, currency)}/visit</div>
                            {record.expectedVisitsPerPeriod > 0 && (
                              <div className="text-xs text-gray-500">Est. {record.expectedVisitsPerPeriod} visits/{record.paymentFrequency?.toLowerCase() || 'period'}</div>
                            )}
                          </>
                        ) : (
                          <>
                            <div>Basic: {formatMoney(record.basicSalary, currency)}</div>
                            <div className="text-xs text-gray-500">Allowances: {formatMoney(totalAllowances, currency)}</div>
                            {record.pf?.applicable && pfTotal > 0 && (
                              <div className="text-xs text-gray-500">PF: {formatMoney(pfTotal, currency)}</div>
                            )}
                            {record.esic?.applicable && esicTotal > 0 && (
                              <div className="text-xs text-gray-500">ESIC: {formatMoney(esicTotal, currency)}</div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        <div>{formatMoney(record.netSalary ?? record.basicSalary + totalAllowances - totalDeductions, currency)}</div>
                        <div className="text-xs font-normal text-gray-500">{record.paymentFrequency}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${record.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {record.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {record.revisionReason && (
                          <div className="text-xs text-gray-500 mt-1">{record.revisionReason}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => setModalState({ open: true, mode: 'edit', record })}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Icon name="Edit" size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(record)}
                            disabled={deletingId === record._id}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            {deletingId === record._id ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Trash2" size={16} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
          <span>
            Showing {(pagination.page - 1) * PAGE_SIZE + 1} - {Math.min(pagination.page * PAGE_SIZE, pagination.total || records.length)} of {pagination.total || records.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(-1)}
              disabled={filters.page <= 1}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span>Page {filters.page} / {totalPages}</span>
            <button
              onClick={() => handlePageChange(1)}
              disabled={filters.page >= totalPages}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>
      </div>

      <SalaryFormModal
        open={modalState.open}
        mode={modalState.mode}
        initialData={modalState.record}
        employeeOptions={employeeOptions}
        employeesLoading={employeesLoading}
        onClose={() => setModalState({ open: false, mode: 'create', record: null })}
        onSubmit={currentModalSubmit}
      />
    </div>
  );
};

export default SalaryTab;
